import React, { useEffect, useRef, useState } from "react";
import { createJob, updateJob } from "../services/apiService";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import FormInput from "./FormInput";
import FormSelect from "./FormSelect";
import {
  buildPersistedJobPayload,
  DEFAULT_NEW_ESTIMATE_CHANGE_NOTE,
  DEFAULT_UPDATE_ESTIMATE_CHANGE_NOTE,
} from "../utils/jobPayload";

export const SAVE_ABANDONED_MESSAGE =
  "Estimate NOT saved — your changes are still unsaved.";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

function JobDetailsModal({
  mode,
  formData,
  dispatch,
  currentJob,
  viewingVersionNumber = null,
  onJobSaved,
  onClose,
}) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [changeNote, setChangeNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const saveCompletedRef = useRef(false);
  const abandonWarnedRef = useRef(false);

  const isEditing = !!currentJob?.id;
  const isSaveMode = mode === "save";

  /* Saving while an older version is on screen is legal and sometimes exactly
     what you want — but it makes what you are looking at the new latest
     version, which is not obvious from a dialog that just says "Save". Say it
     out loud; never block it. */
  const latestVersionNumber = currentJob?.version_count ?? null;
  const isSavingFromOlderVersion =
    isSaveMode &&
    isEditing &&
    viewingVersionNumber != null &&
    latestVersionNumber != null &&
    viewingVersionNumber < latestVersionNumber;

  useEffect(() => {
    if (!mode) return;
    saveCompletedRef.current = false;
    abandonWarnedRef.current = false;
    if (!isSaveMode) return;
    setChangeNote(
      isEditing
        ? DEFAULT_UPDATE_ESTIMATE_CHANGE_NOTE
        : DEFAULT_NEW_ESTIMATE_CHANGE_NOTE,
    );
  }, [mode, isSaveMode, isEditing]);

  const handleSave = async () => {
    if (!isAuthenticated) {
      setError("Please log in to save jobs");
      return;
    }

    // The dialog's own name field is the source of truth in BOTH modes. Pinning
    // the saved job's name while editing meant a rename typed right here was
    // dropped on the way to the server — the estimate came back with its old
    // name and no hint that anything had been ignored. The saved values are a
    // fallback for a blank field, never an override of a filled one.
    const jobName = formData?.job_name?.trim() || currentJob?.job_name;
    if (!jobName?.trim()) {
      setError("Job Name is required.");
      return;
    }

    if (!changeNote.trim()) {
      setError("Please provide a change note.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const dataToSave = {
        ...buildPersistedJobPayload(formData),
        job_name: formData.job_name?.trim() || currentJob?.job_name,
        customer_name:
          formData.customer_name ?? currentJob?.customer_name ?? "",
        change_note: changeNote,
      };

      let result;
      if (isEditing) {
        result = await updateJob(currentJob.id, dataToSave);
      } else {
        result = await createJob(dataToSave);
      }

      saveCompletedRef.current = true;
      setChangeNote("");
      setError(null);

      if (onJobSaved) {
        onJobSaved(result);
      }
      onClose();
    } catch (err) {
      console.error("Error saving job:", err);
      setError(err.message || "Failed to save job");
    } finally {
      setIsSaving(false);
    }
  };

  const warnIfSaveAbandoned = () => {
    if (!isSaveMode || saveCompletedRef.current || abandonWarnedRef.current) {
      return;
    }
    abandonWarnedRef.current = true;
    toast.warning(SAVE_ABANDONED_MESSAGE);
  };

  const handleClose = () => {
    warnIfSaveAbandoned();
    setChangeNote("");
    setError(null);
    onClose();
  };

  const canSave = isSaveMode
    ? changeNote.trim() && (isEditing || formData?.job_name?.trim())
    : true;

  return (
    <Dialog
      open={!!mode}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="job-details-modal">
        <DialogHeader>
          <DialogTitle>
            {isSaveMode
              ? isEditing
                ? "Save Changes"
                : "Save New Estimate"
              : "Edit Job Details"}
          </DialogTitle>
          <DialogDescription>
            {isSaveMode
              ? "Review job details and add a change note before saving."
              : "Update the job name, customer, and status."}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="error-message" style={{ margin: 0 }}>
            {error}
          </p>
        )}

        <div className="job-details-modal__fields">
          <div className="job-details-modal__grid">
            <FormInput
              label="Job Name & #"
              field="job_name"
              value={formData.job_name}
              dispatch={dispatch}
              required={true}
            />
            <FormInput
              label="Customer Name"
              field="customer_name"
              value={formData.customer_name}
              dispatch={dispatch}
            />
          </div>
          <FormSelect
            label="Status"
            field="status"
            value={formData.status || "draft"}
            options={STATUS_OPTIONS}
            dispatch={dispatch}
          />

          {isSaveMode && (
            <div className="job-details-modal__change-note">
              <label htmlFor="modal-changeNote" className="form-label">
                Change Note <span className="required-indicator">*</span>
              </label>
              <Textarea
                id="modal-changeNote"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder={
                  isEditing
                    ? "Describe what changed in this version"
                    : "Initial version notes"
                }
                rows={3}
              />
            </div>
          )}
        </div>

        {isSavingFromOlderVersion ? (
          <p className="job-details-modal__version-note" role="status">
            You’re saving from version {viewingVersionNumber} of{" "}
            {latestVersionNumber}. This saves as version{" "}
            {latestVersionNumber + 1} and becomes the latest — versions 1–
            {latestVersionNumber} are kept.
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          {isSaveMode ? (
            <Button onClick={handleSave} disabled={isSaving || !canSave}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          ) : (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default JobDetailsModal;
